import { format, parse } from "date-fns";
import { Link } from "react-router";

import { pluralize } from "@/components/helpers/string-utils";
import { CertificationThumbnail } from "@/components/pages_logged_in/certifications/CertificationThumbnail";
import { intervals } from "@/components/pages_logged_in/certifications/types";
import { ExpiringIcon } from "@/components/pieces/icons/svg/ExpiringIcon";
import { FailureIcon } from "@/components/pieces/icons/svg/FailureIcon";
import { SuccessIcon } from "@/components/pieces/icons/svg/SuccessIcon";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CertificationsQuery, ExpirationType } from "@/graphql";

function StatusBadge({
  status,
  total,
  icon,
  color,
}: {
  status: string;
  total: number;
  icon: React.ReactNode;
  color: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge className={`text-${color}-800 bg-${color}-100`}>
          {icon}
          {total}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{status}</TooltipContent>
    </Tooltip>
  );
}

export function CertificationCard({
  certification,
}: {
  certification: NonNullable<
    CertificationsQuery["currentWorkspace"]
  >["certifications"]["data"][number];
}) {
  return (
    <Card className="py-4 sm:py-6" asChild>
      <Link to={`/certifications/${certification.id}`}>
        <div className="flex sm:px-6 items-start">
          <div className="hidden sm:flex border shrink-0">
            <CertificationThumbnail
              thumbnail={certification.thumbnail}
              alt={`${certification.name} certificate thumbnail`}
              className="w-[126px] h-[90px]"
            />
          </div>
          <div className="w-full flex flex-col gap-2.5 sm:gap-3">
            <CardHeader className="px-4 sm:px-6 gap-0 sm:gap-1">
              <CardTitle className="text-base/snug font-semibold sm:text-lg/snug">
                {certification.name}
              </CardTitle>
              <CardDescription className="text-base/snug text-gray-500">
                {certification.expirationType === ExpirationType.NoExpiration
                  ? "No expiration"
                  : certification.expirationType === ExpirationType.Relative
                    ? "Expires after " +
                      pluralize({
                        amount: certification.expirationValue,
                        singular:
                          certification.expirationInterval?.toLowerCase() || "",
                        plural: certification.expirationInterval
                          ? intervals[certification.expirationInterval]
                          : "",
                      })
                    : "Expires on " +
                      format(
                        parse(
                          certification.expirationDate || "",
                          "yyyy-MM-dd",
                          new Date(),
                        ),
                        "MMM d, yyyy",
                      )}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex gap-3 px-4 sm:px-6">
              <StatusBadge
                status="Certified"
                total={certification.participants.totalActive}
                icon={<SuccessIcon color="var(--color-green-800)" />}
                color="green"
              />
              {certification.participants.totalExpiringSoon > 0 && (
                <StatusBadge
                  status="Expiring Soon"
                  total={certification.participants.totalExpiringSoon}
                  icon={<ExpiringIcon color="var(--color-yellow-800)" />}
                  color="yellow"
                />
              )}
              {certification.participants.totalExpired > 0 && (
                <StatusBadge
                  status="Expired"
                  total={certification.participants.totalExpired}
                  icon={<ExpiringIcon color="var(--color-red-800)" />}
                  color="red"
                />
              )}
              {certification.participants.totalRevoked > 0 && (
                <StatusBadge
                  status="Revoked"
                  total={certification.participants.totalRevoked}
                  icon={<FailureIcon color="var(--color-red-800)" />}
                  color="red"
                />
              )}
            </CardContent>
          </div>
        </div>
      </Link>
    </Card>
  );
}
